import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";
import {DocumentType} from "app/services/domain/aws-ssm-document.service";

import {BaseListService, BatchResponse} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";
import {BaseComponent} from "framework/ui/components";

@Injectable()
export class AwsSsmExecutionService extends BaseListService<Models.AwsSsmListDocumentExecutionRequest, Models.BatchRequest, Models.AwsSsmJob>
{
    constructor(inj: Injector)
    {
        super(inj, Models.AwsSsmJob);
    }

    @ReportError
    public list(listReq: Models.AwsSsmListDocumentExecutionRequest): Promise<Models.ListResponse>
    {
        return this.domain.apis.awsSsm.executionList(this.app.domain.users.lastActiveTenantId, listReq);
    }


    @ReportError
    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<BatchResponse<Models.AwsSsmJob>>
    {
        const recordIdBatchRequest = Models.RecordIdentityBatchRequest.newInstance({
                                                                                       ids: req?.ids.map((id) => Models.RecordIdentity.newInstance({
                                                                                                                                                       sysId: id
                                                                                                                                                   }))
                                                                                   });

        return this.domain.apis.awsSsm.executionBatch(this.app.domain.users.lastActiveTenantId, recordIdBatchRequest);

    }

    @ReportError
    public stepList(executionId: string): Promise<Models.ListResponse>
    {
        return this.domain.apis.awsSsmAutomation.executionStepList(this.app.domain.users.lastActiveTenantId, executionId);
    }

    @ReportError
    public async getStepBatchResponse(req: Models.BatchRequest): Promise<Models.AwsSsmExecutionStepBatchResponse>
    {
        const recordIdBatchRequest = Models.RecordIdentityBatchRequest.newInstance({ids: req.ids.map((id) => Models.RecordIdentity.newInstance({sysId: id}))});

        return this.domain.apis.awsSsmAutomation.executionStepBatch(this.app.domain.users.lastActiveTenantId, recordIdBatchRequest);
    }

    @ReportError
    public instanceList(commandId: string): Promise<Models.ListResponse>
    {
        return this.domain.apis.awsSsmCommand.executionInstanceList(this.app.domain.users.lastActiveTenantId, commandId);
    }

    @ReportError
    public async getInstanceBatchResponse(req: Models.BatchRequest): Promise<Models.AwsSsmCommandInstanceExecutionBatchResponse>
    {
        const recordIdBatchRequest = Models.RecordIdentityBatchRequest.newInstance({ids: req.ids.map((id) => Models.RecordIdentity.newInstance({sysId: id}))});

        return this.domain.apis.awsSsmCommand.executionInstanceBatch(this.app.domain.users.lastActiveTenantId, recordIdBatchRequest);
    }

    public async fetchGroupBatchResponse(rowObj: Models.AwsSsmJob): Promise<Models.AwsSsmJobGroup>
    {
        const req = Models.RecordIdentity.newInstance({sysId: rowObj.group_job_id});

        const recordIdBatchRequest = Models.RecordIdentityBatchRequest.newInstance({ids: [req]});

        const groupBatchResponse = await this.app.domain.apis.awsSsm.executionGroupsBatch(this.app.domain.users.lastActiveTenantId, recordIdBatchRequest);
        return groupBatchResponse.results[rowObj.group_job_id];
    }

    public async fetchRerunVersionRecord(jobResponse: Models.AwsSsmJobGroup): Promise<string>
    {
        const versionRequest = Models.RecordIdentityBatchRequest.newInstance({ids: [Models.RecordIdentity.newInstance({sysId: jobResponse.document_version})]});

        const versionDetails: Models.AwsSsmDocumentVersionDetails[] = await this.app.domain.awsSsmDocument.getVersionBatch(jobResponse.document_id, versionRequest);
        return versionDetails[0].document_version;
    }

    @ReportError
    private async executeDetailsPrefill(rowObj: Models.AwsSsmJob,
                                        groupJobResponse: Models.AwsSsmJobGroup): Promise<Models.AwsSsmExecuteCommandDocumentRequest>
    {
        const commandRequest = Models.AwsSsmExecuteCommandDocumentRequest.newInstance({});
        let commandExecution: Models.AwsSsmCommandExecution;

        if (rowObj.execution_parameters instanceof Models.AwsSsmCommandExecution)
        {
            commandExecution = rowObj.execution_parameters;
        }

        commandRequest.cloud_account_id      = rowObj.cloud_account_id;
        commandRequest.region                = rowObj.region;
        commandRequest.document_name         = rowObj.document_name;
        commandRequest.document_version      = await this.fetchRerunVersionRecord(groupJobResponse);
        commandRequest.max_concurrency       = commandExecution.max_concurrency;
        commandRequest.max_errors            = commandExecution.max_errors;
        commandRequest.comment               = commandExecution.comment;
        commandRequest.timeout_seconds       = commandExecution.delivery_timeout_seconds;
        commandRequest.output_s3_key_prefix  = commandExecution.output_s3_key_prefix;
        commandRequest.output_s3_bucket_name = commandExecution.output_s3_bucket_name;
        commandRequest.targets               = commandExecution.targets;
        commandRequest.instance_ids          = commandExecution.instance_ids;
        commandRequest.service_role_arn      = commandExecution.service_role;
        commandRequest.parameters            = {};
        for (const param in commandExecution.parameters)
        {
            commandRequest.parameters[param] = JSON.parse(commandExecution.parameters[param]);
        }
        if (groupJobResponse?.change_id)
        {
            commandRequest.change_id = groupJobResponse.change_id;
        }
        const cloudWatchOutputConfig = commandExecution.cloudwatch_output_config;

        commandRequest.cloud_watch_output_config = Models.AwsSsmCommandDocumentCloudWatchOutputConfig.newInstance({
                                                                                                                      cloud_watch_output_enabled: !!cloudWatchOutputConfig.output_enabled,
                                                                                                                      cloud_watch_log_group_name: cloudWatchOutputConfig.log_group_name
                                                                                                                  });
        if (commandExecution?.notification_config)
        {
            const notificationConfig = commandExecution.notification_config;

            commandRequest.notification_config = Models.AwsSsmNotificationConfig.newInstance({
                                                                                                 arn              : notificationConfig.arn,
                                                                                                 notification_type: notificationConfig.notification_type,
                                                                                                 events           : notificationConfig.events
                                                                                             });
        }
        return commandRequest;
    }

    @ReportError
    public async reRun(currentComponent: BaseComponent,
                       rowObj: Models.AwsSsmJob): Promise<void>
    {
        const executionId = rowObj.execution_id;
        const titleText   = "Rerun Command " + executionId;
        const content     = `Confirm that you want to rerun this command with the same set of <br/> parameters as
                                <span class='clr-cs-blue'>${executionId}</span>`;

        const groupJobResponse = await this.fetchGroupBatchResponse(rowObj);
        const commandRequest   = await this.executeDetailsPrefill(rowObj, groupJobResponse);

        if (await currentComponent.confirmOperation(content, titleText))
        {
            await this.app.domain.apis.awsSsmCommand.commandExecute(this.app.domain.users.lastActiveTenantId, groupJobResponse.document_id, commandRequest);
            this.app.framework.errors.success("Command Rerun Initiated", -1);
        }
    }

    @ReportError
    public async copyToNew(currentComponent: BaseComponent,
                           rowObj: Models.AwsSsmJob): Promise<void>
    {
        const groupJobResponse = await this.fetchGroupBatchResponse(rowObj);
        const commandRequest   = await this.executeDetailsPrefill(rowObj, groupJobResponse);
        const filterOptions    = await this.app.domain.awsSsmDocument.fetchFilters(DocumentType.Command);

        const req   = Models.BatchRequest.newInstance({ids: [groupJobResponse.document_id]});
        const batch = await this.app.domain.awsSsmDocument.batchAsLookup(req);

        const awsDocumentDetail = batch[groupJobResponse.document_id];
        await this.app.domain.awsSsmDocument.executeDialog(currentComponent, awsDocumentDetail, filterOptions, DocumentType.Command, commandRequest);
    }

    public isJson(value: string): boolean
    {
        if (value == "null" || typeof value !== "string") return false;
        try
        {
            let parsedObj = JSON.parse(value);
            // JSON.parse('200') returns true which should not be the case when framing Download icon
            return typeof parsedObj === "object";
        }
        catch (e)
        {
            return false;
        }
    }

    protected createBatchRequest(listReq: Models.AwsSsmListDocumentExecutionRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }
}

export enum ExecutionStatusScope
{
    Success   = "Success",
    Failed    = "Failed",
    Cancelled = "Cancelled",
    TimedOut  = "TimedOut",
    Pending   = "Pending"
}
